import { formatDate } from 'src/helpers/Date';
import type { PurchasePolicyInput } from '../../redux/reducer/v2/purchasePolicy';
import type { ErrorListOutput } from '../../types';

export interface CreateApplicationSuccessResponse {
  createApplication: {
    id: string;
    buildingId: string;
  };
}

export interface CreateApplicationErrorResponse {
  createApplication: ErrorListOutput & {
    id: never;
    buildingId: never;
  };
}

export type CreateApplicationResponse =
  | CreateApplicationSuccessResponse
  | CreateApplicationErrorResponse;

type PickedPurchasePolicyInputForCreateApplication = Pick<
  PurchasePolicyInput,
  | 'email'
  | 'buildingId'
  | 'buildingName'
  | 'address'
  | 'coverageStart'
  | 'firstName'
  | 'lastName'
  | 'buildingUnit'
  | 'phone'
>;
export type CreateApplicationInputData =
  Partial<PickedPurchasePolicyInputForCreateApplication>;

interface CreateApplicationArgs {
  application: CreateApplicationInputData;
  utmSource: string | undefined;
}

function getBuildingAddressQueryString({
  address,
  buildingId,
  buildingName,
}: {
  address: PickedPurchasePolicyInputForCreateApplication['address'] | undefined;
  buildingId: string | undefined;
  buildingName: string | undefined;
}) {
  if (buildingId) {
    return `buildingId: "${buildingId}",`;
  }

  if (buildingName) {
    return `buildingName: "${buildingName}",`;
  }

  if (address) {
    return `address: {
      street: "${address.street}",
      shortNameStreet: "${address.shortNameStreet}",
      municipality: "${address.municipality}",
      addressNumber: "${address.addressNumber}",
      region: ${address.state},
      postalCode: "${address.postalCode}",
    },`;
  }

  return '';
}

function getCoverageStartQueryString(coverageStart: string | undefined | null) {
  if (!coverageStart) {
    return '';
  }

  const formattedCoverageStart = formatDate(coverageStart);
  if (!formattedCoverageStart) {
    return '';
  }

  return `coverageStart: "${formattedCoverageStart}",`;
}

export const CREATE_APPLICATION = ({
  application,
  utmSource,
}: CreateApplicationArgs) => {
  const coverageStart = getCoverageStartQueryString(application.coverageStart);
  const buildingAddress = getBuildingAddressQueryString({
    address: application.address,
    buildingId: application.buildingId,
    buildingName: application.buildingName,
  });
  const utmSourceQuery = utmSource ? `utmSource: "${utmSource}",` : '';
  const buildingUnitValue =
    application.buildingUnit !== undefined ? application.buildingUnit : '';
  const phone = application.phone ? `phone: "${application.phone}",` : ``;

  return `mutation {
    createApplication(input: {
      email: "${application.email}",
      firstName: "${application.firstName}",
      lastName: "${application.lastName}",
      buildingUnit: "${buildingUnitValue}",
      ${buildingAddress}
      ${coverageStart}
      ${utmSourceQuery}
      ${phone}
    }) {
      ... on ApplicationOutput {
        id
        buildingId
      }
      ... on ErrorListOutput {
        errors {
          type
          message
        }
      }
    }
  }`;
};
